/**
 * Palette tables (PRD §3.5, §5.5).
 *
 * Per-archetype color "moods" — each archetype ships a small array of
 * candidate palettes; the cosmetic seed picks one. Each palette has a
 * primary + secondary + accent + emissive slot.
 *
 * **Reference aesthetic** (PRD §6.3): the user's MagicaVoxel reference
 * is olive-green chassis + dark-metal joints + bright-orange chevron
 * emissives + occasional red accents. The default palette for each
 * archetype takes that as its anchor; subsequent palettes are seasonal
 * variations (winter teal, desert tan, etc.) that keep the same
 * dark-joint + bright-emissive contrast structure intact.
 *
 * Slot rationale:
 *   - primary   : chassis bulk — large, low-saturation, matte-ish.
 *   - secondary : joints / feet / under-plates — dark, near-black.
 *   - accent    : trim panels and detail stripes — saturated mid-tone.
 *   - emissive  : chevrons / heat vents / eye-slit — bloom magnet.
 *
 * Adding a palette = append below. Adding an archetype mood = add a
 * key. No code change anywhere else.
 */

import type { Archetype, MaterialSlot } from "./types";

type Palette = Record<MaterialSlot, string>;

// Anchor palette — the canonical olive/orange MagicaVoxel reference.
// Every archetype gets a `[0]` variant of this shape so a fresh seed
// always rolls a "looks like the reference" mech for the showcase.
const REFERENCE_OLIVE: Palette = {
  primary: "#6b6f3a", // olive drab
  secondary: "#1f1f1f", // near-black joints + feet
  accent: "#3a3a26", // darker olive trim
  emissive: "#ff6a1c", // signature orange chevron
};

export const ARCHETYPE_PALETTES: Record<Archetype, ReadonlyArray<Palette>> = {
  sniper: [
    REFERENCE_OLIVE,
    { primary: "#4a5f3a", secondary: "#1a1a1a", accent: "#2c3a23", emissive: "#7ee8fa" }, // jungle / cyan optics
    { primary: "#2c3e50", secondary: "#101418", accent: "#1a242f", emissive: "#ff3b3b" }, // gunmetal / red dot
  ],
  brawler: [
    REFERENCE_OLIVE,
    { primary: "#7a3a2a", secondary: "#1c1410", accent: "#3b1f17", emissive: "#ff8a3c" }, // rust + flame
    { primary: "#5a3a4a", secondary: "#160f18", accent: "#2c1d28", emissive: "#ffb347" }, // bruise + amber
  ],
  scout: [
    REFERENCE_OLIVE,
    { primary: "#3a5a4a", secondary: "#0f1a16", accent: "#22332b", emissive: "#9ffca1" }, // forest + glow-green
    { primary: "#4a5560", secondary: "#101418", accent: "#283038", emissive: "#bde0fe" }, // ice
  ],
  generalist: [
    REFERENCE_OLIVE,
    { primary: "#5e5b3a", secondary: "#191712", accent: "#322f1f", emissive: "#ffd166" }, // tan/desert + amber
    { primary: "#4a4a4a", secondary: "#161616", accent: "#262626", emissive: "#ff6a1c" }, // neutral gray
  ],
  support: [
    REFERENCE_OLIVE,
    { primary: "#3a4a5e", secondary: "#0e131a", accent: "#1f2a36", emissive: "#ffadad" }, // medic pink
    { primary: "#6b5b3a", secondary: "#1a1610", accent: "#33291f", emissive: "#fff3b0" }, // beacon yellow
  ],
};
